/**
 * sdkBiddingPlacements.js - ESM twin of the chain half of proxy-server/sdkBidding/placements.js
 * (packForIu, placementChain). The report import / CSV parse is server-only and has no twin.
 * Pinned by sdkBiddingPlacementsParity.test.js. Edit both files together.
 *
 * One Meta win = GAM row + its joined Meta ad request. Four links, fixed order:
 *   config (pubsetting, ancestor-union) -> GAM response rendering_data.placement_id
 *   -> Meta SDK request PLACEMENT_ID -> Meta response definition.placement_id.
 * Only the FIRST disagreeing link raises meta_placement_chain_mismatch. resolved_placement_id and
 * the app-id prefix are evidence only. Ids compare by the part after the last `_`. Never throws.
 */

import { unitEntries } from './sdkBiddingMapping';

export const CODES = {
  chain: 'meta_placement_chain_mismatch',
  unknown: 'meta_placement_unknown',
  unit: 'meta_placement_unit_mismatch',
  format: 'meta_placement_format_mismatch',
  // config level: the pubsetting config against the imported report
  configUnknown: 'meta_placement_config_unknown',
  notInCapture: 'meta_placement_not_in_capture',
  dead: 'meta_placement_dead',
};

const DAY_MS = 864e5;
export const STALE_DAYS = 30;
export const DEAD_MIN_BID_REQUESTS = 10000;
export const DEAD_MAX_RESPONSE_RATE = 0.01;
export const NO_REPORT_NOTE = 'No Meta placement report imported. Import the Monetization Manager CSV in Config, App Packs.';
export const NO_MAPPING_NOTE = 'No pubsetting config captured for this app: the placement config check needs a MITM capture that includes the app\'s pubsetting request.';
export const STALE_NOTE = 'Report older than 30 days: dead and not-seen findings may be out of date.';

export const USB_NOTE = 'Needs a MITM capture: placement checks read the pubsetting config, the GAM response and the Meta response, which logcat does not carry.';
const LINK_WORDS = {
  config_gam: ['GAM config', 'GAM response'],
  gam_sdk: ['GAM response', 'Meta SDK request'],
  sdk_definition: ['Meta SDK request', 'Meta response'],
};

/** Strip Excel `="..."` wrapping, then keep the part after the last `_` (twin of placements.cleanId). */
function cleanId(cell) {
  const s = String(cell == null ? '' : cell).replace(/[="]/g, '').trim();
  const i = s.lastIndexOf('_');
  return (i >= 0 ? s.slice(i + 1) : s).trim();
}

const sfx = (id) => {
  if (id == null) return null;
  const s = cleanId(String(id));
  return s || null;
};

function urlParam(url, name) {
  const u = String(url || '');
  const q = u.includes('?') ? u.slice(u.indexOf('?') + 1) : '';
  try { return new URLSearchParams(q).get(name); } catch { return null; }
}

function platformOfUrl(url) {
  const js = urlParam(url, 'js') || '';
  if (/^afma-sdk-i-/i.test(js)) return 'ios';
  if (/^afma-sdk-a-/i.test(js)) return 'android';
  return null;
}

/** Pack for a GAM iu: enabled, holds a report, networkId === first iu segment; registry order wins. */
export function packForIu(packs, iu) {
  try {
    const seg = String(iu || '').split('/').filter(Boolean)[0];
    if (!seg || !packs || typeof packs !== 'object') return null;
    const list = Array.isArray(packs) ? packs : Object.values(packs);
    for (const p of list) {
      if (!p || typeof p !== 'object' || p.enabled === false) continue;
      const rep = p.metaPlacementReport;
      if (!rep || typeof rep !== 'object' || !Object.keys(rep).length) continue;
      if (p.identity && String(p.identity.networkId) === seg) return p;
    }
    return null;
  } catch { return null; }
}

function emptyChain(status, notes) {
  return {
    status,
    notes,
    links: { config: [], gamResponse: null, sdkRequest: null, definition: null },
    firstBreak: null,
    evidence: { resolvedPlacementId: null, resolvedDiffers: false, appIdPrefix: null, definitionType: null, templateId: null, width: null, height: null, sz: [] },
    issues: [],
  };
}

export function placementChain(gamRow, metaReq, ctx) {
  try {
    if (gamRow && gamRow.captureSource === 'logcat') return emptyChain('na', [USB_NOTE]);
    const c = ctx && typeof ctx === 'object' ? ctx : {};
    const row = gamRow && typeof gamRow === 'object' ? gamRow : {};
    const ex = (row.validData && row.validData.expanded) || {};
    const out = emptyChain('ok', []);
    const mapping = c.mapping || null;
    const unit = c.unit || urlParam(row.url, 'iu') || '';
    const platform = (mapping && mapping.platform) || c.platform || platformOfUrl(row.url) || null;
    const list = c.list !== undefined ? c.list : (c.report && platform ? c.report[platform] || null : null);

    const gamResp = sfx(ex.sdkPlacementId);
    const meta = metaReq && typeof metaReq === 'object' ? metaReq : null;
    const sdk = meta ? sfx(meta.placementId) : null;
    const def = meta && meta.definition ? sfx(meta.definition.placementId) : null;
    const cfgSet = [];
    for (const e of unitEntries(mapping, unit)) {
      if (e && e.buyer === 'meta') { const s = sfx(e.placementId); if (s && !cfgSet.includes(s)) cfgSet.push(s); }
    }
    out.links = { config: cfgSet, gamResponse: gamResp, sdkRequest: sdk, definition: def };

    if (!cfgSet.length) out.notes.push('unit mapping not captured');
    if (!gamResp) out.notes.push('GAM response placement not captured');
    if (!meta) out.notes.push('SDK request not captured');
    else if (!def) out.notes.push('Meta response not captured');

    const rowKey = meta && meta.rowKey != null ? meta.rowKey : (row._k != null ? row._k : (row.id != null ? row.id : null));
    const bid = ex.sdkBidId || (meta && meta.bidId) || null;
    const mk = (code, msg) => ({ level: 'warn', phase: 'winner', code, msg, rowKey });
    const where = `unit ${unit || '(unknown)'}${bid ? `, bid ${bid}` : ''}`;

    let brk = null;
    if (cfgSet.length && gamResp && !cfgSet.includes(gamResp)) brk = { link: 'config_gam', left: cfgSet.join(', '), right: gamResp };
    else if (gamResp && sdk && gamResp !== sdk) brk = { link: 'gam_sdk', left: gamResp, right: sdk };
    else if (sdk && def && sdk !== def) brk = { link: 'sdk_definition', left: sdk, right: def };
    if (brk) {
      const [l, r] = LINK_WORDS[brk.link];
      out.firstBreak = brk;
      out.issues.push(mk(CODES.chain, `Meta placement chain breaks between ${l} and ${r}: ${l} has placement ${brk.left}, ${r} has ${brk.right} (${where}). Meta can only fill the placement the GAM config names.`));
    }

    const placements = list && Array.isArray(list.placements) ? list.placements : null;
    if (placements && gamResp) {
      const entry = placements.find((p) => p && p.placementId === gamResp) || null;
      if (!entry) {
        out.issues.push(mk(CODES.unknown, `Meta won with placement ${gamResp}, which is not in the imported Meta placement report for ${platform || 'this platform'} (${where}). Re-import the report if the placement is new.`));
      } else {
        const d = entry.derivedUnit;
        if (d && unit && !(unit === d || unit.startsWith(`${d}/`))) {
          const mapped = unitEntries(mapping, unit).some((e) => e && e.buyer === 'meta' && sfx(e.placementId) === gamResp);
          if (!mapped) out.issues.push(mk(CODES.unit, `Placement ${gamResp} is named for ${d} in Meta's report but served ${unit}, and the GAM config does not map it to that unit (${where}).`));
        }
        const size = entry.derivedSize;
        const szRaw = urlParam(row.url, 'sz');
        const szList = szRaw ? szRaw.split(/\||%7C/i).filter(Boolean) : [];
        out.evidence.sz = szList;
        if (size && szList.length && !szList.includes(size)) {
          out.issues.push(mk(CODES.format, `Placement ${gamResp} is a ${size} placement in Meta's report, but the GAM request asked for ${szList.join(', ')} (${where}).`));
        }
      }
    }
    if (!out.evidence.sz.length) {
      const szRaw = urlParam(row.url, 'sz');
      out.evidence.sz = szRaw ? szRaw.split(/\||%7C/i).filter(Boolean) : [];
    }

    const resolved = sfx(ex.sdkResolvedPlacementId);
    out.evidence.resolvedPlacementId = resolved;
    out.evidence.resolvedDiffers = !!(resolved && gamResp && resolved !== gamResp);
    if (c.packMetaAppId && ex.sdkPlacementId) {
      const full = String(ex.sdkPlacementId);
      const i = full.indexOf('_');
      const prefix = i > 0 ? full.slice(0, i) : null;
      out.evidence.appIdPrefix = { prefix, expected: String(c.packMetaAppId), matches: prefix === String(c.packMetaAppId) };
    }
    if (meta) {
      out.evidence.definitionType = meta.definition ? meta.definition.type || null : null;
      out.evidence.templateId = meta.templateId == null ? null : meta.templateId;
      out.evidence.width = meta.width == null ? null : meta.width;
      out.evidence.height = meta.height == null ? null : meta.height;
    }

    out.status = brk ? 'mismatch' : (out.notes.length ? 'partial' : 'ok');
    return out;
  } catch {
    return emptyChain('partial', ['placement chain could not be evaluated']);
  }
}

// Config half (twin of placements.js evaluateConfigPlacements / captureTimeMs).

function rowMs(r) {
  if (!r || typeof r !== 'object') return NaN;
  if (typeof r.startedAt === 'number' && Number.isFinite(r.startedAt)) return r.startedAt;
  for (const k of ['timestamp', 'ts']) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v) { const ms = Date.parse(v); if (Number.isFinite(ms)) return ms; }
  }
  return NaN;
}

/** Earliest Meta network_ads_common request time, else earliest GAM (`google`) row, else NaN. */
export function captureTimeMs(rows) {
  try {
    const list = Array.isArray(rows) ? rows : [];
    const earliest = (pred) => {
      let best = NaN;
      for (const r of list) {
        if (!r || typeof r !== 'object' || !pred(r)) continue;
        const ms = rowMs(r);
        if (Number.isFinite(ms) && (!Number.isFinite(best) || ms < best)) best = ms;
      }
      return best;
    };
    const meta = earliest((r) => /network_ads_common/.test(String(r.url || '')) && /facebook\.com/.test(String(r.url || '')));
    if (Number.isFinite(meta)) return meta;
    return earliest((r) => r.requestType === 'google');
  } catch { return NaN; }
}

const nz = (v) => { const n = Number(v); return Number.isFinite(n) && n >= 0 ? n : 0; };

export function evaluateConfigPlacements(mapping, list, opts) {
  const platform = (mapping && typeof mapping === 'object' && mapping.platform) || null;
  const shell = (state, note) => ({ state, note, platform, counts: { known: 0, unknown: 0, notSeen: 0, dead: 0 }, findings: [], suppressed: [], stale: false });
  try {
    if (!mapping || typeof mapping !== 'object' || mapping.source !== 'pubsetting' || !mapping.units || typeof mapping.units !== 'object') return shell('no_mapping', NO_MAPPING_NOTE);
    const placements = list && Array.isArray(list.placements) ? list.placements : null;
    if (!placements) return shell('no_report', NO_REPORT_NOTE);

    const o = opts && typeof opts === 'object' ? opts : {};
    const toMs = Date.parse(`${list.window && list.window.to}T00:00:00Z`);
    const stale = typeof o.captureMs === 'number' && Number.isFinite(o.captureMs) && Number.isFinite(toMs)
      && (o.captureMs - toMs) > STALE_DAYS * DAY_MS;

    const cfg = new Map();
    for (const unit of Object.keys(mapping.units)) {
      const entries = Array.isArray(mapping.units[unit]) ? mapping.units[unit] : [];
      for (const e of entries) {
        if (!e || e.buyer !== 'meta') continue;
        const id = sfx(e.placementId);
        if (!id) continue;
        if (!cfg.has(id)) cfg.set(id, []);
        if (!cfg.get(id).includes(unit)) cfg.get(id).push(unit);
      }
    }
    const byId = new Map();
    for (const p of placements) if (p && p.placementId != null && !byId.has(String(p.placementId))) byId.set(String(p.placementId), p);

    const out = shell('ok', stale ? STALE_NOTE : null);
    out.stale = stale;
    if (stale) out.suppressed = [CODES.dead, CODES.notInCapture];
    for (const [id, units] of cfg) {
      const p = byId.get(id);
      if (!p) {
        out.counts.unknown += 1;
        out.findings.push({ code: CODES.configUnknown, level: 'warn', placementId: id, name: null, units, text: `Placement ${id} is mapped on ${units.join(', ')} in the GAM config but is not in the imported Meta placement report for ${platform || 'this platform'}. Re-import the report if the placement is new, or fix the config.` });
        continue;
      }
      out.counts.known += 1;
      const m = p.last30 || {};
      const req = nz(m.bidReq);
      const resp = nz(m.bidResp);
      if (nz(m.imps) === 0 && req >= DEAD_MIN_BID_REQUESTS && resp / req < DEAD_MAX_RESPONSE_RATE && !stale) {
        out.counts.dead += 1;
        out.findings.push({ code: CODES.dead, level: 'warn', placementId: id, name: p.name || null, units, text: `Placement ${id}${p.name ? ` (${p.name})` : ''} had ${req} Meta bid requests and ${resp} responses but 0 impressions in the last 30 days of the report. It is mapped on ${units.join(', ')} and looks dead.` });
      }
    }
    if (!stale) {
      for (const [id, p] of byId) {
        if (cfg.has(id)) continue;
        const imps = nz(p.last30 && p.last30.imps);
        if (imps > 0) {
          out.counts.notSeen += 1;
          out.findings.push({ code: CODES.notInCapture, level: 'info', placementId: id, name: p.name || null, units: [], text: `Not seen in this capture: placement ${id}${p.name ? ` (${p.name})` : ''} served ${imps} impressions in the last 30 days of the report, but no unit in the captured GAM config maps it.` });
        }
      }
    }
    return out;
  } catch {
    return shell('no_mapping', NO_MAPPING_NOTE);
  }
}
